import { useState, useRef, useEffect } from "react";
import { FiChevronDown, FiChevronUp, FiX } from "react-icons/fi";
import { Check } from "lucide-react";

import { ReactComponent as FiltersCancel } from "../../assets/images/icons/filters-cancel.svg";

/**
 * FilterDropdown - Reusable multi-select dropdown for filter panel
 */
const FilterDropdown = ({ label, options = [], selectedValues = [], onAdd, onRemove }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const displayText = selectedValues.length > 0 ? `${selectedValues.length} Selected` : "All";

  return (
    <div className="space-y-2">
      <p className="text-[#FEFCEB] text-[15px] font-medium m-0">{label}</p>
      <div className="relative" ref={ref}>
        <button
          onClick={() => setIsOpen((v) => !v)}
          className={`w-full h-11 bg-[#021529] rounded-full px-4 flex items-center justify-between transition-colors border ${
            isOpen ? "border-[#00E7FF]" : "border-[#192634]"
          }`}
        >
          <span className="text-[#FEFCEB] text-[15px]">{displayText}</span>
          {isOpen ? (
            <FiChevronUp className="w-4 h-4 text-[#FEFCEB] shrink-0" />
          ) : (
            <FiChevronDown className="w-4 h-4 text-[#FEFCEB] shrink-0" />
          )}
        </button>

        {isOpen && options.length > 0 && (
          <div className="absolute top-[calc(100%+4px)] left-0 right-0 bg-[#021529] border border-[#192634] rounded-2xl overflow-hidden z-20 max-h-52 overflow-y-auto shadow-xl">
            {options.map((option, i) => {
              const value = typeof option === "string" ? option : option._id || option.name;
              const displayLabel = typeof option === "string" ? option : option.name || option.title;
              const isSelected = selectedValues.includes(value);
              return (
                <button
                  key={i}
                  onClick={() => (isSelected ? onRemove(value) : onAdd(value))}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors text-left border-b border-[#192634] last:border-b-0"
                >
                  <div
                    className={`w-4 h-4 rounded border-[1.5px] flex items-center justify-center shrink-0 transition-colors ${
                      isSelected ? "border-[#00E7FF] text-[#00E7FF]" : "border-[#333E48]"
                    }`}
                  >
                    {isSelected && <Check size={10} strokeWidth={3} />}
                  </div>
                  <span className="text-[#FEFCEB] text-[14px]">{displayLabel}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {selectedValues.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1">
          {selectedValues.map((val, i) => (
            <div
              key={i}
              className="flex items-center gap-1.5 bg-app-card border border-[#192634] px-3 py-1.5 rounded-full"
            >
              <span className="text-[#FEFCEB] text-[13px]">{val}</span>
              <button
                onClick={() => onRemove(val)}
                className="text-[#FEFCEB] hover:opacity-60 transition-opacity flex items-center"
              >
                <FiX className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * FilterPanel - Slide-out filter panel for fixtures
 */
const FilterPanel = ({
  isOpen,
  onClose,
  showConflicts,
  setShowConflicts,
  phases = [],
  selectedPhases = [],
  onPhaseChange,
  brackets = [],
  selectedBrackets = [],
  onBracketChange,
  pitches = [],
  selectedPitches = [],
  onPitchChange,
  teams = [],
  selectedTeams = [],
  onTeamChange,
  onClearAll,
  onApply,
}) => {
  if (!isOpen) return null;

  const hasActiveFilters =
    selectedPhases.length > 0 ||
    selectedBrackets.length > 0 ||
    selectedPitches.length > 0 ||
    selectedTeams.length > 0;

  return (
    <div className="fixed top-0 right-0 w-full sm:w-[400px] h-screen z-[1000] bg-[#000F1F] border-l border-[#192634] text-[#FEFCEB] flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex justify-between items-center px-6 py-5 border-b border-[#192634]">
        <h3 className="text-[20px] font-semibold m-0">Filters</h3>
        <button
          onClick={onClose}
          className="p-1 hover:bg-white/10 rounded-full transition-colors"
        >
          <FiltersCancel className="w-5 h-5" />
        </button>
      </div>

      {/* Filter Content */}
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
        {/* Show Conflicts */}
        <label className="flex items-center gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showConflicts}
            onChange={(e) => setShowConflicts?.(e.target.checked)}
            className="sr-only peer"
          />
          <div
            className={`w-4 h-4 rounded border-[1.5px] flex items-center justify-center transition-colors ${
              showConflicts ? "border-[#00E7FF] text-[#00E7FF]" : "border-[#333E48]"
            }`}
          >
            {showConflicts && <Check size={10} strokeWidth={3} />}
          </div>
          <span className="text-[#FEFCEB] text-[15px]">Show Conflicts</span>
        </label>

        <FilterDropdown
          label="Phase"
          options={phases}
          selectedValues={selectedPhases}
          onAdd={(v) => onPhaseChange?.(v, true)}
          onRemove={(v) => onPhaseChange?.(v, false)}
        />

        <FilterDropdown
          label="Bracket"
          options={brackets}
          selectedValues={selectedBrackets}
          onAdd={(v) => onBracketChange?.(v, true)}
          onRemove={(v) => onBracketChange?.(v, false)}
        />

        <FilterDropdown
          label="Pitches"
          options={pitches}
          selectedValues={selectedPitches}
          onAdd={(v) => onPitchChange?.(v, true)}
          onRemove={(v) => onPitchChange?.(v, false)}
        />

        <FilterDropdown
          label="Teams"
          options={teams}
          selectedValues={selectedTeams}
          onAdd={(v) => onTeamChange?.(v, true)}
          onRemove={(v) => onTeamChange?.(v, false)}
        />
      </div>

      {/* Footer */}
      <div className="px-6 py-5 border-t border-[#192634] flex gap-3 justify-end">
        <button
          onClick={onClearAll}
          className="h-11 px-6 bg-app-card text-[#FEFCEB] rounded-full hover:opacity-50 transition-opacity font-medium text-[15px]"
        >
          Clear All
        </button>
        <button
          onClick={onApply}
          disabled={!hasActiveFilters}
          className="h-11 px-6 bg-[#00E7FF] text-[#021529] rounded-full font-medium text-[15px] hover:opacity-50 transition-opacity disabled:opacity-30 disabled:cursor-not-allowed"
        >
          Apply
        </button>
      </div>
    </div>
  );
};

export default FilterPanel;
